import type { FlagState } from '$models/FeatureFlags/FeatureFlags';
import type { Order } from '$models/Order/Order';
import { page as pageStore } from '$app/stores';
import { getCookie } from '$lib/cookies';
import { getOrderPricesProductsPrice, listSubscriptions } from '$lib/webparking';
import { isValid } from 'date-fns';
import { get } from 'svelte/store';

const featureFlagsCookie = getCookie('tsc_feature_flags');

type DataLayerEvent = Record<string, unknown>;

type WindowWithDataLayer = Window & {
	dataLayer?: DataLayerEvent[];
};

export function ensureDataLayer() {
	if (typeof window === 'undefined') {
		return [] as DataLayerEvent[];
	}

	const windowWithDataLayer = window as WindowWithDataLayer;
	windowWithDataLayer.dataLayer = windowWithDataLayer.dataLayer || [];

	return windowWithDataLayer.dataLayer;
}

function pushData(data: DataLayerEvent) {
	ensureDataLayer().push(data);

	// console.log('pushData', data);
}

function roundDecimals(number) {
	return Math.round((parseFloat(number) + Number.EPSILON) * 100) / 100;
}

function formatFeatureFlags() {
	if (!featureFlagsCookie) {
		return {};
	}

	try {
		const flags = JSON.parse(featureFlagsCookie);

		let formattedFlags: string[] = [];

		Object.entries(flags).forEach(([flagName, flagValue]) => {
			formattedFlags = [...formattedFlags, `${flagName}:${flagValue ? 'true' : 'false'}`];
		});

		return {
			user_properties: {
				feature_flags: formattedFlags.join('|')
			},
			feature_flags: formattedFlags.join('|')
		};
	} catch (e) {
		return {};
	}
}

async function formatItems(order: Order, isUrgent = false, price: number | undefined) {
	const page = await get(pageStore);
	const products = page.data.products;
	const items = order.products || [];
	const extraItems = order.nonSubscribableProducts || [];
	const itemsQuantity = items.reduce((acc, item) => {
		return acc + item.quantity;
	}, 0);
	const paymentStatus = order.paymentStatus ?? 'not_started';
	let itemsPrice = price ?? 0;
	let itemPrice = 0;

	if (!itemsPrice) {
		if (items.length && paymentStatus !== 'paid' && itemsQuantity > 0) {
			itemsPrice = await getOrderPricesProductsPrice(order, order.frequency);
		} else if (paymentStatus === 'paid') {
			/* If an order is paid, we have all the data we need on the order object.
			 * Discounts with a maximum use of 1 can not be calculated anymore anyway.
			 */
			itemsPrice = order.total!; // When order status is paid, order total is always a number

			// Substract potential extra items price. We want to calculate regular item price.
			const extraItemsTotal = extraItems.reduce((acc, item) => acc + parseFloat(item.price), 0);

			if (extraItemsTotal > 0) {
				itemsPrice = itemsPrice - extraItemsTotal;
			}
		}
	}

	if (itemsPrice && itemsQuantity) {
		itemPrice = roundDecimals(itemsPrice / itemsQuantity);
	}

	return [
		...items.map((orderItem) => {
			const item = products.regular.find((p) => p.id === orderItem.id);
			if (!item) {
				return;
			}
			return {
				item_id: item.sku,
				item_name: item.name,
				price: itemPrice.toFixed(2),
				quantity: orderItem.quantity,
				item_category: item?.category?.name || 'Onbekend',
				item_category2: item.category.id
			};
		}),
		...extraItems.map((orderItem) => {
			const item = products.nonSubscribable.find((p) => p.id === orderItem.id);
			if (!item) {
				return;
			}
			return {
				item_id: item.name, // Non-subs don't have skus,
				item_name: item.name,
				price: parseFloat(item.price),
				quantity: orderItem.quantity,
				item_category: 'Los product'
			};
		})
	];
}

export function getCustomerType(): 'existing' | 'new' {
	let customerType: 'existing' | 'new' = 'existing';
	try {
		let registeredAt = localStorage.getItem('registered_at');

		if (registeredAt) {
			registeredAt = new Date(registeredAt);
			const timePassedMs = new Date().getTime() - registeredAt.getTime();
			if (timePassedMs < 24 * 60 * 60 * 1000) {
				customerType = 'new';
			} else {
				localStorage.removeItem('registered_at');
			}
		}
	} catch (error) {
		// Fail silently
	}

	return customerType;
}

export function trackEvent(eventName) {
	pushData({
		event: eventName
	});

	// console.log('trackEvent', eventName)
}

export function trackVirtualPageview(url, title) {
	const data = {
		event: 'virtualPageview',
		pageUrl: url,
		pageTitle: title,
		...formatFeatureFlags()
	};

	// console.log('trackVirtualPageview', data);

	pushData(data);
}

export async function trackViewItem(order, price: number | undefined = undefined) {
	const data = {
		event: 'view_item',
		ecommerce: {
			items: await formatItems(order, false, price)
		},
		...formatFeatureFlags()
	};

	pushData(data);
}

export async function trackAddToCart(cart, price: number | undefined = undefined) {
	const data = {
		event: 'add_to_cart',
		pageUrl: window.location.href,
		ecommerce: {
			frequency: cart.frequency ?? 0,
			items: await formatItems(cart, false, price)
		},
		...formatFeatureFlags()
	};

	pushData(data);
}

export async function trackExperimentAssignment(flags: FlagState) {
	const data = {
		event: 'experiment_assignment',
		user_properties: { ...flags }
	};

	pushData(data);
}

export async function trackCheckoutStepOne(cart) {
	try {
		const data = {
			event: 'begin_checkout',
			ecommerce: {
				frequency: cart.frequency,
				items: await formatItems(cart, false)
			},
			...formatFeatureFlags()
		};
		trackVirtualPageview(
			window.location.origin + '/afrekenen/e-mailadres',
			'Afrekenen - E-mailadres'
		);
		pushData(data);

		// console.log('data1', data);
	} catch (error) {
		// console.log('step1 error', error);
	}
}

export async function trackCheckoutStepTwo(cart) {
	try {
		const data = {
			event: 'add_shipping_info',
			ecommerce: {
				frequency: cart.frequency,
				customer_type: getCustomerType(),
				items: await formatItems(cart, false)
			},
			...formatFeatureFlags()
		};

		trackVirtualPageview(window.location.origin + '/afrekenen/gegevens', 'Afrekenen - Gegevens');
		pushData(data);

		// console.log('data2', data);
	} catch (error) {
		// console.log('step2 error', error);
	}
}

export async function trackCheckoutStepThree(cart) {
	try {
		const data = {
			event: 'add_payment_info',
			ecommerce: {
				frequency: cart.frequency,
				customer_type: getCustomerType(),
				items: await formatItems(cart, false)
			},
			...formatFeatureFlags()
		};

		trackVirtualPageview(window.location.origin + '/afrekenen/afronden', 'Afrekenen - Afronden');
		pushData(data);

		// console.log('data3', data);
	} catch (error) {
		// console.log('step3 error', error);
	}
}

/*
Formula provided by client:
CASE
WHEN client_type = 'nieuwe klant' & order_type = 'one-off' then 27 
WHEN client_type = 'nieuwe klant' & order_type = 'parent' then 70
WHEN client_type = 'bestaande klant' & order_type = 'parent' & first_subscription = true then 83
WHEN client_type = 'bestaande klant' & order_type = 'parent' & first_subscription = false then 27
ELSE total_amount * 0.39 
END
*/
async function calculateECLV(order: Order) {
	const page = await get(pageStore);
	const customerType = getCustomerType();
	const orderType = order.frequency > 0 ? 'parent' : 'one-off';
	const eclvValues = page.data.eclvValues;
	let isFirstSubscription = customerType === 'new'; // By default true when customer is new

	try {
		const userSubscriptions = await listSubscriptions();
		isFirstSubscription = userSubscriptions.length <= 1;
	} catch (e) {
		// Fail silently and use default value
	}

	if (customerType === 'new' && orderType === 'one-off') {
		return eclvValues.newOneoff;
	}

	if (customerType === 'new' && orderType === 'parent') {
		return eclvValues.newParent;
	}

	if (customerType === 'existing' && orderType === 'parent' && isFirstSubscription) {
		return eclvValues.existingParentFirstSubscription;
	}

	if (customerType === 'existing' && orderType === 'parent' && !isFirstSubscription) {
		return eclvValues.existingParentNotFirstSubscription;
	}

	return roundDecimals((order.total ?? 0) * eclvValues.defaultMultiplier);
}

export async function trackCheckoutStepFour(order: Order, isUrgentOrder, customer) {
	if (order.paymentStatus !== 'paid' && !isUrgentOrder) {
		return;
	}

	const orderType = order.frequency > 0 ? 'Parent' : 'One-time';

	const data = {
		event: 'purchase',
		email: customer.email,
		ecommerce: {
			frequency: order.frequency,
			transaction_id: order.id,
			value: parseFloat(order.total).toFixed(2),
			currency: 'EUR',
			eclv: await calculateECLV(order),
			customer_type: getCustomerType(),
			order_type: isUrgentOrder ? 'Urgent' : orderType,
			coupon: JSON.parse(localStorage.getItem('coupon')) || null,
			items: await formatItems(order, isUrgentOrder)
		},
		...formatFeatureFlags()
	};

	trackVirtualPageview(window.location.origin + '/afrekenen/bedankt', 'Afrekenen - Bedankt');
	pushData(data);

	// console.log('data4', data);
}
